import { useCallback, useEffect, useRef, useState } from 'react';

// RMS одного PCM16-кадра в [0..1] — источник высоты столбиков waveform.
export function frameRms(int16) {
  if (!int16 || !int16.length) return 0;
  let sumSq = 0;
  for (let i = 0; i < int16.length; i++) {
    const v = int16[i] / 32768;
    sumSq += v * v;
  }
  return Math.sqrt(sumSq / int16.length);
}

// Чистый обработчик сообщений AudioWorklet-порта (вынесен для тестируемости в jsdom,
// где нет реального AudioWorkletNode). Два вида сообщений:
//  - {type:'flushed'} — ack на flush: хвост допакован и отправлен → onFlushed()
//    (клиент резолвит промис flush() и только потом шлёт finalize);
//  - Int16Array — PCM-кадр: всегда обновляем уровень (waveform), но на паузе
//    (isPaused()) НЕ форвардим в onPcm — иначе нули track.enabled=false биллятся квотой.
export function makeWorkletMessageHandler({ onPcm, onLevel, onFlushed, isPaused }) {
  return (event) => {
    const data = event.data;
    if (data && data.type === 'flushed') {
      onFlushed?.();
      return;
    }
    onLevel?.(frameRms(data));
    if (isPaused?.()) return;
    onPcm?.(data);
  };
}

// useStreamingCapture — захват аудио для live-распознавания.
// Источник: микрофон (моно, 16kHz) либо микрофон + системный звук
// (getDisplayMedia, смикшированный в моно через ChannelMerger — оба источника
// сведены в канал 0, второй канал merger'а не используется; за merger'ом —
// GainNode 0.5, компенсирующий сумму двух источников, иначе громкие участки
// клиппируют). Дальше сигнал идёт в AudioWorklet (`public/pcm-worklet.js`,
// ресемпл до 16kHz, PCM16-кадры по 300мс), который вызывает `onPcm(int16)`
// на каждый кадр.
//
// Пауза: pausedRef=true гейтит форвард PCM (нули не льются на сервер → квота не
// тратится), плюс track.enabled=false ради приватности (микрофон реально молчит).
// Сервер финализирует текущую фразу по тишине (резолюция оркестратора №3).
export default function useStreamingCapture({ withSystemAudio = false, onPcm, onSourceEnded } = {}) {
  const [level, setLevel] = useState(0);
  const [deviceLabel, setDeviceLabel] = useState('');

  const audioContextRef = useRef(null);
  const micStreamRef = useRef(null);
  const displayStreamRef = useRef(null);
  const workletNodeRef = useRef(null);
  const gainNodeRef = useRef(null); // только withSystemAudio — см. start()
  const pausedRef = useRef(false);
  const flushResolversRef = useRef([]); // ожидающие ack 'flushed' резолверы flush()

  // Колбэки могут переопределяться на каждый рендер родителя — держим последние
  // версии в ref, чтобы не пересоздавать start()/подписки воркалета от их смены.
  const onPcmRef = useRef(onPcm);
  const onSourceEndedRef = useRef(onSourceEnded);
  onPcmRef.current = onPcm;
  onSourceEndedRef.current = onSourceEnded;

  const resolveFlush = useCallback(() => {
    const resolvers = flushResolversRef.current;
    flushResolversRef.current = [];
    resolvers.forEach((r) => r());
  }, []);

  const stop = useCallback(() => {
    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = null;
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (gainNodeRef.current) {
      gainNodeRef.current.disconnect();
      gainNodeRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
    if (displayStreamRef.current) {
      displayStreamRef.current.getTracks().forEach((track) => track.stop());
      displayStreamRef.current = null;
    }
    resolveFlush();  // не оставить flush()-промисы висеть после остановки
    setLevel(0);
  }, [resolveFlush]);

  const start = useCallback(async () => {
    pausedRef.current = false;

    let displayStream = null;
    if (withSystemAudio) {
      // 🔴 Окно выбора — ПЕРВЫМ, до любых await: getDisplayMedia требует жеста
      // пользователя (≈5 с после клика), а запрос доступа к микрофону и загрузка
      // воркалета этот срок съедают — первая попытка падала, вторая проходила.
      // video: true обязателен: getDisplayMedia({video:false}) Chrome отклоняет
      // TypeError. Видео не используем — 1 кадр/с, дорожка живёт до stop().
      // systemAudio: 'include' — галочка «Звук системы» и для всего экрана (Windows).
      displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 1 } },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
        systemAudio: 'include',
      });
      displayStreamRef.current = displayStream;
      if (displayStream.getAudioTracks().length === 0) {
        // Выбрали окно или сняли галочку звука: писался бы один микрофон, а человек
        // думал бы, что пишется встреча.
        stop();
        const err = new Error('display stream without audio');
        err.userMessage = 'Звук встречи не передан. Выберите вкладку со встречей и включите '
          + '«Поделиться звуком вкладки» (для всего экрана в Windows — «Звук системы»).';
        throw err;
      }
    }

    const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
    audioContextRef.current = audioContext;
    await audioContext.audioWorklet.addModule('/pcm-worklet.js');

    const micStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
      },
    });
    micStreamRef.current = micStream;

    let sourceNode;
    if (displayStream) {
      // Оба источника сведены в один и тот же вход merger'а (канал 0) — сигналы
      // суммируются; второй вход/канал остаётся пустым. Тот же эффект, что давал
      // старый createScriptProcessor(4096, 1, 1) с автоматическим даунмиксом.
      // Сумма двух источников клиппирует на громких участках — гейн 0.5 после
      // merger'а компенсирует удвоение амплитуды (mic-only путь не тронут).
      const displaySource = audioContext.createMediaStreamSource(displayStream);
      const micSource = audioContext.createMediaStreamSource(micStream);
      const merger = audioContext.createChannelMerger(2);
      displaySource.connect(merger, 0, 0);
      micSource.connect(merger, 0, 0);
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 0.5;
      merger.connect(gainNode);
      gainNodeRef.current = gainNode;
      sourceNode = gainNode;

      const displayTrack = displayStream.getAudioTracks()[0];
      if (displayTrack) {
        displayTrack.onended = () => onSourceEndedRef.current?.();
      }
    } else {
      sourceNode = audioContext.createMediaStreamSource(micStream);
    }

    // pcm-processor читает только канал 0 входа (см. public/pcm-worklet.js) —
    // явные channelCount/channelCountMode не нужны ни для моно-мика, ни для merger'а.
    const workletNode = new AudioWorkletNode(audioContext, 'pcm-processor');
    workletNode.port.onmessage = makeWorkletMessageHandler({
      onPcm: (int16) => onPcmRef.current?.(int16),
      onLevel: setLevel,
      onFlushed: resolveFlush,
      isPaused: () => pausedRef.current,
    });
    sourceNode.connect(workletNode);
    workletNodeRef.current = workletNode;

    const micLabel = micStream.getAudioTracks()[0]?.label || 'Микрофон';
    setDeviceLabel(displayStream ? `${micLabel} + звук встречи` : micLabel);
  }, [withSystemAudio, resolveFlush, stop]);

  const pause = useCallback(() => {
    pausedRef.current = true;  // гейт форварда PCM: нули на паузе не биллятся квотой
    micStreamRef.current?.getAudioTracks().forEach((track) => { track.enabled = false; });
    displayStreamRef.current?.getAudioTracks().forEach((track) => { track.enabled = false; });
    setLevel(0);
  }, []);

  const resume = useCallback(() => {
    micStreamRef.current?.getAudioTracks().forEach((track) => { track.enabled = true; });
    displayStreamRef.current?.getAudioTracks().forEach((track) => { track.enabled = true; });
    pausedRef.current = false;
  }, []);

  // Допаковать хвост < 300мс воркалета и ДОЖДАТЬСЯ его отправки (ack 'flushed')
  // перед session.finalize() на стопе. Таймаут 500мс — страховка от зависания,
  // если ack по какой-то причине не придёт.
  const flush = useCallback(() => {
    const node = workletNodeRef.current;
    if (!node) return Promise.resolve();
    return new Promise((resolve) => {
      let done = false;
      const settle = () => { if (!done) { done = true; resolve(); } };
      flushResolversRef.current.push(settle);
      node.port.postMessage('flush');
      setTimeout(settle, 500);
    });
  }, []);

  useEffect(() => () => stop(), [stop]);

  return { start, pause, resume, stop, flush, level, deviceLabel };
}
